import React, { useState } from 'react';
import { EditIcon, CheckIcon, XIcon } from 'lucide-react';
import { unitData } from '../predictions/UnitFilter';
import { supplyClassesData } from '../inputs/supplyClassesData';
export const PriorityOfSupport = () => {
  const [currentPhase, setCurrentPhase] = useState('Phase 2');
  const [isEditing, setIsEditing] = useState(false);
  // Initial priorities
  const [unitPriorities, setUnitPriorities] = useState(['alpha', 'bravo', 'echo']);
  const [supplyPriorities, setSupplyPriorities] = useState(['class-iii', 'class-v', 'class-ix']);
  // Temporary state for editing
  const [tempUnitPriorities, setTempUnitPriorities] = useState([...unitPriorities]);
  const [tempSupplyPriorities, setTempSupplyPriorities] = useState([...supplyPriorities]);
  const [tempPhase, setTempPhase] = useState(currentPhase);
  const handleEdit = () => {
    setTempUnitPriorities([...unitPriorities]);
    setTempSupplyPriorities([...supplyPriorities]);
    setTempPhase(currentPhase);
    setIsEditing(true);
  };
  const handleSave = () => {
    setUnitPriorities(tempUnitPriorities);
    setSupplyPriorities(tempSupplyPriorities);
    setCurrentPhase(tempPhase);
    setIsEditing(false);
  };
  const handleCancel = () => {
    setIsEditing(false);
  };
  const moveItem = (array: string[], index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === array.length - 1) {
      return array;
    }
    const newArray = [...array];
    const offset = direction === 'up' ? -1 : 1;
    [newArray[index], newArray[index + offset]] = [newArray[index + offset], newArray[index]];
    return newArray;
  };
  const getUnitName = (unitId: string) => {
    const unit = unitData.find(u => u.id === unitId);
    if (!unit) return unitId;
    if (unit.type === 'company' && unit.parentId) {
      const battalion = unitData.find(u => u.id === unit.parentId);
      return `${unit.name}, ${battalion?.name || ''}`;
    }
    return unit.name;
  };
  const getSupplyClassName = (supplyClassId: string) => {
    const supplyClass = supplyClassesData.find(s => s.id === supplyClassId);
    return supplyClass ? `CL ${supplyClass.romanNumeral} (${supplyClass.name})` : supplyClassId;
  };
  return <div className="bg-white rounded-lg shadow p-3 border border-gray-200" data-id="element-522">
      <div className="flex justify-between items-center mb-2" data-id="element-523">
        <div data-id="element-524">
          <h2 className="text-base font-semibold text-gray-900" data-id="element-525">
            Priority of Support
            {!isEditing && <span className="text-sm font-normal text-gray-600 ml-2" data-id="element-526">
                {currentPhase}
              </span>}
          </h2>
          {isEditing && <input type="text" value={tempPhase} onChange={e => setTempPhase(e.target.value)} className="mt-1 block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" data-id="element-527" />}
        </div>
        {!isEditing ? <button onClick={handleEdit} className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100" data-id="element-528">
            <EditIcon className="w-4 h-4" data-id="element-529" />
          </button> : <div className="flex space-x-2" data-id="element-530">
            <button onClick={handleSave} className="p-1 rounded-md text-green-500 hover:text-green-700 hover:bg-green-100" data-id="element-531">
              <CheckIcon className="w-4 h-4" data-id="element-532" />
            </button>
            <button onClick={handleCancel} className="p-1 rounded-md text-red-500 hover:text-red-700 hover:bg-red-100" data-id="element-533">
              <XIcon className="w-4 h-4" data-id="element-534" />
            </button>
          </div>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-535">
        {/* Units Priority - Condensed */}
        <div data-id="element-536">
          <h3 className="font-medium text-gray-800 text-sm mb-1" data-id="element-537">Units</h3>
          {!isEditing ? <p className="text-sm text-gray-700" data-id="element-538">
              {unitPriorities.map((unitId, index) => <span key={unitId} data-id="element-539">
                  {index > 0 && ' → '}
                  {getUnitName(unitId)}
                </span>)}
            </p> : <div className="space-y-1" data-id="element-540">
              {tempUnitPriorities.map((unitId, index) => <div key={unitId} className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded" data-id="element-541">
                  <span data-id="element-542">
                    {index + 1}. {getUnitName(unitId)}
                  </span>
                  <div className="flex space-x-1" data-id="element-543">
                    <button onClick={() => setTempUnitPriorities(moveItem(tempUnitPriorities, index, 'up'))} disabled={index === 0} className={`text-gray-500 ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-700'}`} data-id="element-544">
                      ↑
                    </button>
                    <button onClick={() => setTempUnitPriorities(moveItem(tempUnitPriorities, index, 'down'))} disabled={index === tempUnitPriorities.length - 1} className={`text-gray-500 ${index === tempUnitPriorities.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-700'}`} data-id="element-545">
                      ↓
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
        {/* Supply Priority - Condensed */}
        <div data-id="element-546">
          <h3 className="font-medium text-gray-800 text-sm mb-1" data-id="element-547">Supply</h3>
          {!isEditing ? <p className="text-sm text-gray-700" data-id="element-548">
              {supplyPriorities.map((supplyId, index) => <span key={supplyId} data-id="element-549">
                  {index > 0 && ' → '}
                  {getSupplyClassName(supplyId)}
                </span>)}
            </p> : <div className="space-y-1" data-id="element-550">
              {tempSupplyPriorities.map((supplyId, index) => <div key={supplyId} className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded" data-id="element-551">
                  <span data-id="element-552">
                    {index + 1}. {getSupplyClassName(supplyId)}
                  </span>
                  <div className="flex space-x-1" data-id="element-553">
                    <button onClick={() => setTempSupplyPriorities(moveItem(tempSupplyPriorities, index, 'up'))} disabled={index === 0} className={`text-gray-500 ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-700'}`} data-id="element-554">
                      ↑
                    </button>
                    <button onClick={() => setTempSupplyPriorities(moveItem(tempSupplyPriorities, index, 'down'))} disabled={index === tempSupplyPriorities.length - 1} className={`text-gray-500 ${index === tempSupplyPriorities.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-700'}`} data-id="element-555">
                      ↓
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>
    </div>;
};